/**
 * Syntax Color Ramps - Configuración de rampas de color para métricas de sintaxis
 *
 * Define las paletas de color utilizadas para visualizar métricas Space Syntax
 * como Integration y Choice. Cada rampa mapea valores normalizados [0,1] a colores.
 *
 * @example
 * ```typescript
 * import { COLOR_RAMPS, getColorRampStops, SYNTAX_RAMP_NAMES } from './syntax-color-ramps';
 *
 * // Obtener stops para rampa spectral
 * const stops = getColorRampStops('spectral', 0, 100);
 * // [0, '#5e4fa2'], [25, '#3288bd'], [50, '#abdda4'], [75, '#fdae61'], [100, '#d53e4f']
 * ```
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Punto de parada en una rampa de color
 */
export interface ColorStop {
  /** Valor normalizado entre 0 y 1 */
  stop: number;
  /** Color en formato hexadecimal */
  color: string;
}

/**
 * Definición de una rampa de color
 */
export interface ColorRamp {
  /** Nombre identificador */
  name: string;
  /** Nombre para mostrar en UI */
  label: string;
  /** Puntos de parada de color */
  stops: ColorStop[];
  /** Descripción opcional */
  description?: string;
}

/**
 * Nombres de las rampas disponibles
 */
export type SyntaxRampName = 'spectral' | 'viridis' | 'magma' | 'turbo' | 'rdylbu';

// ============================================================================
// COLOR RAMPS
// ============================================================================

/**
 * Rampa Spectral - Classic diverging colormap
 * Azul (bajo) → Verde (medio) → Rojo (alto)
 */
const SPECTRAL: ColorRamp = {
  name: 'spectral',
  label: 'Spectral',
  description: 'Divergente clásica: azul-verde-rojo',
  stops: [
    { stop: 0.0, color: '#5e4fa2' },
    { stop: 0.25, color: '#3288bd' },
    { stop: 0.5, color: '#abdda4' },
    { stop: 0.75, color: '#fdae61' },
    { stop: 1.0, color: '#d53e4f' },
  ],
};

/**
 * Rampa Viridis - Perceptually uniform
 * Morado oscuro → Azul → Verde → Amarillo
 */
const VIRIDIS: ColorRamp = {
  name: 'viridis',
  label: 'Viridis',
  description: 'Perceptualmente uniforme',
  stops: [
    { stop: 0.0, color: '#440154' },
    { stop: 0.25, color: '#3b528b' },
    { stop: 0.5, color: '#21918c' },
    { stop: 0.75, color: '#5ec962' },
    { stop: 1.0, color: '#fde725' },
  ],
};

/**
 * Rampa Magma - Dark to bright
 * Negro → Morado → Rojo → Amarillo
 */
const MAGMA: ColorRamp = {
  name: 'magma',
  label: 'Magma',
  description: 'Oscuro a brillante',
  stops: [
    { stop: 0.0, color: '#000004' },
    { stop: 0.25, color: '#51127c' },
    { stop: 0.5, color: '#b73779' },
    { stop: 0.75, color: '#fc8961' },
    { stop: 1.0, color: '#fcfdbf' },
  ],
};

/**
 * Rampa Turbo - Rainbow-like with better perceptual properties
 * Azul → Cian → Verde → Amarillo → Rojo
 */
const TURBO: ColorRamp = {
  name: 'turbo',
  label: 'Turbo',
  description: 'Arcoíris mejorado',
  stops: [
    { stop: 0.0, color: '#30123b' },
    { stop: 0.25, color: '#4662d7' },
    { stop: 0.5, color: '#36b677' },
    { stop: 0.75, color: '#f5d540' },
    { stop: 1.0, color: '#7a0403' },
  ],
};

/**
 * Rampa RdYlBu - Red-Yellow-Blue diverging
 * Rojo → Naranja → Amarillo → Azul claro → Azul
 */
const RDYLBU: ColorRamp = {
  name: 'rdylbu',
  label: 'Red-Yellow-Blue',
  description: 'Divergente rojo-amarillo-azul',
  stops: [
    { stop: 0.0, color: '#313695' },
    { stop: 0.25, color: '#74add1' },
    { stop: 0.5, color: '#ffffbf' },
    { stop: 0.75, color: '#f46d43' },
    { stop: 1.0, color: '#a50026' },
  ],
};

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Todas las rampas de color disponibles
 */
export const COLOR_RAMPS: Record<SyntaxRampName, ColorRamp> = {
  spectral: SPECTRAL,
  viridis: VIRIDIS,
  magma: MAGMA,
  turbo: TURBO,
  rdylbu: RDYLBU,
};

/**
 * Lista de nombres de rampas para selección en UI
 */
export const SYNTAX_RAMP_NAMES: SyntaxRampName[] = ['spectral', 'viridis', 'magma', 'turbo', 'rdylbu'];

/**
 * Rampa por defecto
 */
export const DEFAULT_SYNTAX_RAMP: SyntaxRampName = 'spectral';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Obtiene los stops de una rampa escalados a un rango dado
 *
 * @param rampName - Nombre de la rampa
 * @param min - Valor mínimo del rango
 * @param max - Valor máximo del rango
 * @returns Array de [value, color] para usar en MapLibre interpolate
 */
export function getColorRampStops(
  rampName: SyntaxRampName,
  min: number,
  max: number,
): Array<number | string> {
  const ramp = COLOR_RAMPS[rampName];
  const range = max - min;

  const result: Array<number | string> = [];
  for (const { stop, color } of ramp.stops) {
    result.push(min + stop * range);
    result.push(color);
  }

  return result;
}

/**
 * Genera una expresión MapLibre de interpolación para colorear por métrica
 *
 * @param rampName - Nombre de la rampa
 * @param metricProperty - Propiedad GeoJSON que contiene el valor
 * @param min - Valor mínimo
 * @param max - Valor máximo
 * @returns Expresión MapLibre para line-color o fill-color
 */
export function createSyntaxColorExpression(
  rampName: SyntaxRampName,
  metricProperty: string,
  min: number,
  max: number,
): unknown[] {
  const stops = getColorRampStops(rampName, min, max);

  return [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', metricProperty], min],
    ...stops,
  ];
}

/**
 * Obtiene el color para un valor dado usando una rampa
 *
 * @param rampName - Nombre de la rampa
 * @param value - Valor a colorear
 * @param min - Valor mínimo del rango
 * @param max - Valor máximo del rango
 * @returns Color hexadecimal
 */
export function getColorForValue(
  rampName: SyntaxRampName,
  value: number,
  min: number,
  max: number,
): string {
  const ramp = COLOR_RAMPS[rampName];
  const normalized = Math.max(0, Math.min(1, (value - min) / (max - min)));

  // Encontrar los dos stops más cercanos
  let lowerStop = ramp.stops[0];
  let upperStop = ramp.stops[ramp.stops.length - 1];

  for (let i = 0; i < ramp.stops.length - 1; i++) {
    if (normalized >= ramp.stops[i].stop && normalized <= ramp.stops[i + 1].stop) {
      lowerStop = ramp.stops[i];
      upperStop = ramp.stops[i + 1];
      break;
    }
  }

  // Interpolar entre los dos colores
  const t = (normalized - lowerStop.stop) / (upperStop.stop - lowerStop.stop);
  return interpolateColor(lowerStop.color, upperStop.color, t);
}

/**
 * Interpola linealmente entre dos colores hex
 */
function interpolateColor(color1: string, color2: string, t: number): string {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);

  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);

  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
